import type { AdminTicketListItem } from '@planuze/admin-contracts';
import {
  Button,
  Checkbox,
  EmptyState,
  StatusBadge,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@planuze/ui';
import { MessageSquare } from 'lucide-react';
import { useSupportContent } from '../../content/sections/support.js';
import { useSupportInboxContent } from '../../content/sections/support-inbox.js';
import { fmtDate } from '../../lib/format.js';
import { CopyableId } from '../copyable-id.js';
import { priorityTone, statusLabel, statusTone } from './ticket-status.js';

interface Props {
  readonly items: readonly AdminTicketListItem[];
  readonly onRowClick: (id: string) => void;
  /** Há filtros ativos — diferencia "sem resultados" de "sem itens" (ADR-0700/§6). */
  readonly hasFilters: boolean;
  readonly onClearFilters: () => void;
  readonly selectedIds: ReadonlySet<string>;
  readonly onToggle: (id: string) => void;
  readonly onToggleAll: (checked: boolean) => void;
}

/* Tabela de tickets sobre o primitive `Table` de @planuze/ui (ADR-0700). O handler enriquece
   cada ticket com o e-mail do usuário e do atendente; quando o e-mail não é resolvível
   (`null`) a célula cai no id copiável. */
export function InboxTable({
  items,
  onRowClick,
  hasFilters,
  onClearFilters,
  selectedIds,
  onToggle,
  onToggleAll,
}: Props) {
  const copy = useSupportContent();
  const bulkCopy = useSupportInboxContent().bulk;
  if (items.length === 0) {
    return hasFilters ? (
      <EmptyState
        icon={<MessageSquare className="size-6" />}
        title={copy.inboxTable.emptyFilteredTitle}
        description={copy.inboxTable.emptyFilteredDescription}
        primaryAction={
          <Button variant="outline" onClick={onClearFilters}>
            {copy.inboxTable.clearFilters}
          </Button>
        }
      />
    ) : (
      <EmptyState
        icon={<MessageSquare className="size-6" />}
        title={copy.inboxTable.emptyTitle}
        description={copy.inboxTable.emptyDescription}
      />
    );
  }
  const eligible = items.filter((ticket) => ticket.status !== 'closed');
  const allSelected = eligible.length > 0 && eligible.every((ticket) => selectedIds.has(ticket.id));
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-10">
            <Checkbox
              checked={allSelected}
              disabled={eligible.length === 0}
              onCheckedChange={(checked) => onToggleAll(checked === true)}
              aria-label={bulkCopy.selectPage}
            />
          </TableHead>
          <TableHead>{copy.inboxTable.columnSubject}</TableHead>
          <TableHead>{copy.inboxTable.columnUser}</TableHead>
          <TableHead>{copy.inboxTable.columnPriority}</TableHead>
          <TableHead>{copy.inboxTable.columnCreatedAt}</TableHead>
          <TableHead>{copy.inboxTable.columnAssignee}</TableHead>
          <TableHead>{copy.inboxTable.columnStatus}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {items.map((ticket) => (
          <TableRow key={ticket.id}>
            <TableCell>
              <Checkbox
                checked={selectedIds.has(ticket.id)}
                disabled={ticket.status === 'closed'}
                onCheckedChange={() => onToggle(ticket.id)}
                aria-label={bulkCopy.selectRow(ticket.subject)}
              />
            </TableCell>
            <TableCell>
              <button
                type="button"
                className="font-medium text-foreground transition-colors hover:underline"
                onClick={() => onRowClick(ticket.id)}
              >
                {ticket.subject}
              </button>
            </TableCell>
            <TableCell>
              {ticket.userEmail === null ? (
                <CopyableId value={ticket.userId} label={copy.inboxTable.userIdLabel} />
              ) : (
                <span className="text-sm text-foreground">{ticket.userEmail}</span>
              )}
            </TableCell>
            <TableCell>
              <StatusBadge variant={priorityTone(ticket.priority)}>{ticket.priority}</StatusBadge>
            </TableCell>
            <TableCell className="text-muted-foreground">{fmtDate(ticket.createdAt)}</TableCell>
            <TableCell className="text-muted-foreground">
              {ticket.assignedToStaffEmail ?? copy.inboxTable.noAssignee}
            </TableCell>
            <TableCell>
              <StatusBadge variant={statusTone(ticket.status)}>
                {statusLabel(ticket.status, copy.ticketStatusLabels)}
              </StatusBadge>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
